import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";
import "assets/CSS/Switch.css";

const SyncActivityTimeline = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedItems, setExpandedItems] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Summary cards data
  const summaryCardsData = [
    {
      id: 1,
      value: "156",
      label: "Total Syncs",
      icon: "material-symbols:sync-rounded",
      iconColor: "#003A70",
      bgColor: "#f0f0f1",
    },
    {
      id: 2,
      value: "142",
      label: "Successful",
      icon: "heroicons:check-circle",
      iconColor: "#23A26D",
      bgColor: "#E8F5E9",
    },
    {
      id: 3,
      value: "8",
      label: "Warnings",
      icon: "heroicons:exclamation-triangle",
      iconColor: "#DD992F",
      bgColor: "#FFF3E0",
    },
    {
      id: 4,
      value: "6",
      label: "Errors",
      icon: "heroicons:x-circle",
      iconColor: "#D80027",
      bgColor: "#FFEBEE",
    },
  ];

  // Sync activity data
  const syncActivities = [
    {
      id: 1,
      type: "Sync Invoice",
      module: "Invoice",
      status: "success",
      record: "AST-2024-001",
      duration: "2.3s",
      records: 1,
      dateTime: "1/25/2024, 7:30:00 PM",
      details: "Successfully synced assessment to QuickBooks invoice",
      quickbooksId: "INV-2024-001",
    },
    {
      id: 2,
      type: "Sync Invoice",
      module: "Invoice",
      status: "success",
      record: "AST-2024-002",
      duration: "1.8s",
      records: 1,
      dateTime: "1/25/2024, 7:25:00 PM",
      details: "Successfully synced assessment to QuickBooks invoice",
      quickbooksId: "INV-2024-002",
    },
    {
      id: 3,
      type: "Sync Invoice",
      module: "Invoice",
      status: "error",
      record: "AST-2024-003",
      duration: "2.3s",
      records: 1,
      dateTime: "1/25/2024, 7:20:00 PM",
      details: "Failed to sync assessment to QuickBooks invoice",
      errorDetails:
        "Account 'Equipment Rental Revenue' is not mapped to QuickBooks Chart of Accounts",
      errorCode: "MAPPING_ERROR",
      suggestion:
        "Please map this account in Financial Mapping before retrying",
    },
    {
      id: 4,
      type: "Two-way Sync",
      module: "Employees",
      status: "pending",
      record: "AST-2024-004",
      duration: "3.1s",
      records: 1,
      dateTime: "1/25/2024, 7:15:00 PM",
      details: "Sync completed with conflicts that require manual resolution",
      errorDetails:
        "Employee salary updated in both systems - manual resolution required",
      errorCode: "CONFLICT_WARNING",
      suggestion:
        "Review conflict in Employee module and choose which data to keep",
      quickbooksId: "QB-EMP-002",
    },
  ];

  // Common issues data with icons
  const commonIssues = [
    {
      title: "Missing Account Mapping",
      description:
        "Invoice sync fails because revenue accounts aren't mapped to QuickBooks Chart of Accounts.",
      solution: "Complete Financial Mapping setup before syncing invoices.",
      icon: "icon-park-outline:error",
      iconColor: "#D80027",
    },
    {
      title: "QuickBooks Connection Lost",
      description:
        "Sync operations fail due to expired QuickBooks authentication tokens.",
      solution: "Reconnect to QuickBooks Online from the main dashboard.",
      icon: "icon-park-outline:error",
      iconColor: "#D80027",
    },
    {
      title: "Duplicate Customer Names",
      description:
        "Customer sync creates conflicts when similar names exist in both systems.",
      solution:
        "Review customer list and merge or rename duplicates before syncing.",
      icon: "solar:danger-triangle-outline",
      iconColor: "#DD992F",
    },
    {
      title: "Employee Data Conflicts",
      description:
        "Two-way sync creates conflicts when employee data is updated in both systems.",
      solution:
        "Use conflict resolution dialog in Employee module to choose correct data.",
      icon: "solar:danger-triangle-outline",
      iconColor: "#DD992F",
    },
  ];

  const handleToggleExpand = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleViewDetails = (activity) => {
    setSelectedActivity(activity);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedActivity(null);
  };

  const getStatusChip = (status) => {
    const config = {
      success: {
        color: "#E8F5E9",
        textColor: "#23A26D",
        label: "Success",
      },
      error: {
        color: "#FFEBEE",
        textColor: "#D80027",
        label: "Error",
      },
      pending: {
        color: "#FFF3E0",
        textColor: "#DD992F",
        label: "Warning",
      },
    };

    const statusConfig = config[status] || config.success;

    return (
      <Chip
        label={statusConfig.label}
        size="small"
        sx={{
          backgroundColor: statusConfig.color,
          color: statusConfig.textColor,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 24,
        }}
      />
    );
  };

  const handlePageBack = () => {
    navigate("/quickbooks");
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Sync Logs & Error Handling"
    >
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        {/* Single Main Card containing everything */}
        <Card
          sx={{
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Header with Title and Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
                mb: 4,
              }}
            >
              {/* Top Right Buttons */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  startIcon={
                    <Icon
                      icon="heroicons:arrow-up-tray"
                      width={20}
                      height={20}
                    />
                  }
                >
                  Export Log
                </Button>
                <Button
                  variant="contained"
                  color="main"
                  size="medium"
                  startIcon={
                    <Icon icon="heroicons:arrow-path" width={20} height={20} />
                  }
                >
                  Refresh Log
                </Button>
              </Box>
            </Box>

            {/* Summary Cards Section */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {summaryCardsData.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.id}>
                    <Box
                      sx={{
                        backgroundColor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        minHeight: 120,
                      }}
                    >
                      {/* Icon on the left */}
                      <Box
                        sx={{
                          display: "inline-flex",
                          backgroundColor: card.bgColor,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={card.icon}
                          width={24}
                          height={24}
                          style={{ color: card.iconColor }}
                        />
                      </Box>

                      <Box sx={{ textAlign: "right", flex: 1, ml: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="body1">{card.label}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Sync Activity Timeline in Rounded Box */}
            <Card
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                p: 3,
                mb: 4,
              }}
            >
              {/* Search and Filters */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 4,
                }}
              >
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    Sync Activity Timeline
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Real-time view of synchronization events with filtering and
                    error details
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <CustomTextField
                    placeholder="Search sync activities"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      width: 200,
                      "& .MuiOutlinedInput-root": {
                        height: "28px",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon icon="tabler:search" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="success">Success</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                      <MenuItem value="pending">Warning</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Sync Activities List */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {syncActivities.map((activity, index) => (
                  <Card
                    key={activity.id}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                      overflow: "visible",
                    }}
                  >
                    <CardContent sx={{ p: 0, "&:last-child": { pb: 2 } }}>
                      {/* Activity Header */}
                      <Box
                        sx={{
                          p: 3,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => handleToggleExpand(activity.id)}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          {/* Status Icon */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 32,
                              height: 32,
                            }}
                          >
                            <Icon
                              icon={
                                activity.status === "success"
                                  ? "ion:checkmark-done"
                                  : activity.status === "error"
                                  ? "codicon:error"
                                  : "solar:danger-triangle-linear"
                              }
                              width={20}
                              height={20}
                              style={{
                                color:
                                  activity.status === "success"
                                    ? "#23A26D"
                                    : activity.status === "error"
                                    ? "#D80027"
                                    : "#DD992F",
                              }}
                            />
                          </Box>

                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 600, mr: 4 }}
                          >
                            {activity.type}
                          </Typography>
                          {/* Module outlined box */}
                          <Box
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              px: 2,
                              py: 0.5,
                              backgroundColor: "background.paper",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "text.secondary" }}
                            >
                              {activity.module}
                            </Typography>
                          </Box>
                          {getStatusChip(activity.status)}
                        </Box>

                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {/* View Details button only for error and pending status */}
                          {(activity.status === "error" ||
                            activity.status === "pending") && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={
                                <Icon
                                  icon="heroicons:eye"
                                  width={16}
                                  height={16}
                                />
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(activity);
                              }}
                            >
                              View Details
                            </Button>
                          )}
                          <IconButton size="small">
                            <Icon
                              icon={
                                expandedItems[activity.id]
                                  ? "heroicons:chevron-up"
                                  : "heroicons:chevron-down"
                              }
                              width={20}
                              height={20}
                            />
                          </IconButton>
                        </Box>
                      </Box>

                      <Collapse in={expandedItems[activity.id]}>
                        <Box sx={{ p: 3, pt: 0, pb: 0 }}>
                          {/* First Box - Record Details using Grid */}
                          <Card
                            sx={{
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "#003A700D",
                              backgroundColor: "#003A700D",
                              p: 3,
                              mb: 2,
                            }}
                          >
                            <Grid container spacing={4} alignItems="center">
                              <Grid item xs={12} sm={6} md={3}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                  >
                                    Record:
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {activity.record}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                  >
                                    Duration:
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {activity.duration}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                  >
                                    Records:
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {activity.records}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                  >
                                    Date & Time:
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {activity.dateTime}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Card>

                          {/* Second Box - Details and QuickBooks ID side by side */}
                          <Card
                            sx={{
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "#97A4B10D",
                              backgroundColor: "#97A4B10D",
                              p: 4,
                              mb:
                                activity.status === "error" ||
                                activity.status === "pending"
                                  ? 2
                                  : 0,
                            }}
                          >
                            <Grid container spacing={4}>
                              <Grid item xs={12} md={6}>
                                <Box>
                                  <Typography
                                    variant="h5"
                                    sx={{ mb: 1, fontWeight: 600 }}
                                  >
                                    Details
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 530 }}
                                  >
                                    {activity.details}
                                  </Typography>
                                </Box>
                              </Grid>
                              {activity.quickbooksId &&
                                activity.status !== "error" && (
                                  <Grid item xs={12} md={6}>
                                    <Box>
                                      <Typography
                                        variant="h5"
                                        sx={{ mb: 1, fontWeight: 600 }}
                                      >
                                        QuickBooks ID
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 530 }}
                                      >
                                        {activity.quickbooksId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                            </Grid>
                          </Card>

                          {/* Error/Warning Details for error and pending status */}
                          {(activity.status === "error" ||
                            activity.status === "pending") && (
                            <>
                              {/* Error/Warning Details Box */}
                              <Card
                                sx={{
                                  borderRadius: 2,
                                  border: "1px solid",
                                  borderColor:
                                    activity.status === "error"
                                      ? "#DA121280"
                                      : "#DA121280",
                                  backgroundColor:
                                    activity.status === "error"
                                      ? "#FFEBEE"
                                      : "#FFEBEE",
                                  p: 3,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      sx={{ mb: 1, fontWeight: 600 }}
                                    >
                                      {activity.status === "error"
                                        ? "Error Details"
                                        : "Conflict Details"}
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 530 }}
                                    >
                                      {activity.errorDetails}
                                    </Typography>
                                  </Box>

                                  {/* Error/Warning Code and Suggestion */}
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600, mb: 0.5 }}
                                    >
                                      Code: {activity.errorCode}
                                    </Typography>
                                    <Typography variant="body1">
                                      {activity.suggestion}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Card>
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Card>
            <Card
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                p: 4,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                Common Issues & Solutions
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Frequently encountered sync issues and their resolutions
              </Typography>

              {/* Issues Grid - Two boxes side by side */}
              <Grid container spacing={3}>
                {commonIssues.map((issue, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        p: 3,
                        pt: 4,
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            pl: 3,
                          }}
                        >
                          <Icon
                            icon={issue.icon}
                            width={20}
                            height={20}
                            style={{ color: issue.iconColor }}
                          />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {issue.title}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 1, pl: 3 }}>
                        {issue.description}
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "#23A26D" }}
                        >
                          Solution: {issue.solution}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </CardContent>
        </Card>

        {/* View Details Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h4">Sync Error Details</Typography>
            <Typography variant="body1" color="text.secondary">
              Detailed information about the synchronization error
            </Typography>
          </DialogTitle>

          <DialogContent>
            {selectedActivity && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Module, Action, Record ID, Timestamp */}
                <Card
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "#003A700D",
                    backgroundColor: "#003A700D",
                    p: 3,
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 1,
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body1">Module:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedActivity.module}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 1,
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body1">Action:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedActivity.type}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 1,
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body1">Record ID:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedActivity.record}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 1,
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body1">Timestamp:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedActivity.dateTime}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>

                {/* Error Information */}
                <Card
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "#FFEBEE",
                    backgroundColor: "#FFEBEE",
                    p: 3,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Error Information
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        Code:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedActivity.errorCode}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        Message:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 530 }}>
                        {selectedActivity.errorDetails}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        Suggested Resolution:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 530 }}>
                        {selectedActivity.suggestion}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1, justifyContent: "space-between" }}>
            <Button
              variant="contained"
              color="info"
              onClick={handleCloseDialog}
            >
              Cancel
            </Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={
                  <Icon
                    icon="heroicons:wrench-screwdriver"
                    width={20}
                    height={20}
                  />
                }
              >
                Fix Now
              </Button>
              <Button
                variant="contained"
                color="main"
                startIcon={
                  <Icon icon="heroicons:arrow-path" width={20} height={20} />
                }
              >
                Retry Sync
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
    </UserLayout>
  );
};

export default SyncActivityTimeline;
